"use client"

import { useState } from "react"
import TypewriterRendererResponsePlain from "../TypeWriterResponsePlain"
import AIResponse from "../AiResponse"

function ImageTextConverter() {
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [showModal, setShowModal] = useState(false)

  const API_URL = "https://math-image-transcriber-steveszren.replit.app/api/convert-diagram"

  const handleImageUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Preview Image
    const imageURL = URL.createObjectURL(file)
    setImagePreview(imageURL)

    const formData = new FormData()
    formData.append("image", file)

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        body: formData,
      })

      const data = await response.json()
      if (data.success) {
        setResult(data.result)
      } else {
        setError(data.error || "Failed to convert image")
      }
    } catch (err) {
      setError("Error uploading image")
    } finally {
      setIsLoading(false)
    }
  }

  // Handle image paste from clipboard
  const handlePaste = (event) => {
    const items = event.clipboardData?.items
    if (!items) return

    for (const item of items) {
      if (item.type.indexOf("image") === 0) {
        const file = item.getAsFile()
        const imageURL = URL.createObjectURL(file)
        setImagePreview(imageURL)

        const formData = new FormData()
        formData.append("image", file)

        setIsLoading(true)
        setError(null)

        fetch(API_URL, {
          method: "POST",
          body: formData,
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.success) {
              setResult(data.result)
            } else {
              setError(data.error || "Failed to convert image")
            }
          })
          .catch(() => setError("Error uploading image"))
          .finally(() => setIsLoading(false))

        break
      }
    }
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-900 to-gray-900 flex flex-col">
      <header className="bg-black/30 backdrop-blur-sm py-4 px-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-600 p-2 rounded-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <path d="M16 13H8"></path>
              <path d="M16 17H8"></path>
              <path d="M10 9H8"></path>
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white">Math Image to Text Converter</h1>
        </div>
      </header>

      <main className="flex-1 p-4 md:p-6 overflow-auto">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-6 h-full">
          <div className="space-y-6 h-full">
            {/* File input */}
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-4">Upload Image</h2>
              <div className="space-y-4">
                <div className="relative">
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    className="hidden"
                    id="file-upload"
                    onChange={handleImageUpload}
                    disabled={isLoading}
                  />
                  <label
                    htmlFor="file-upload"
                    className="flex items-center justify-center w-full py-3 px-4 bg-white/10 border border-white/20 rounded-lg cursor-pointer hover:bg-white/20 transition-colors text-white"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                    Choose an image
                  </label>
                </div>
                <p className="text-sm text-indigo-200">Supported formats: PNG, JPG, JPEG</p>
              </div>
            </div>

            {/* Paste handler */}
            <div
              className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
              onPaste={handlePaste}
              tabIndex="0"
            >
              <h2 className="text-xl font-semibold text-white mb-4">Paste Image</h2>
              <div
                className="border-2 border-dashed border-indigo-400/30 bg-indigo-900/20 rounded-lg p-6 text-center cursor-pointer hover:bg-indigo-900/30 transition-colors flex flex-col items-center justify-center h-32"
                onPaste={handlePaste}
                tabIndex="0"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-indigo-300 mb-2"
                >
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                </svg>
                <p className="text-indigo-100 font-medium">Click here and paste an image</p>
                <p className="text-sm text-indigo-200 mt-1">Use Ctrl+V or ⌘+V</p>
              </div>
            </div>

            {/* Image Preview */}
            {imagePreview && (
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
                <h2 className="text-xl font-semibold text-white mb-4">Image Preview</h2>
                <div className="flex justify-center bg-black/30 p-4 rounded-lg">
                  <img
                    src={imagePreview || "/placeholder.svg"}
                    alt="Uploaded Preview"
                    className="max-h-60 object-contain rounded-lg cursor-pointer hover:opacity-90 transition"
                    onClick={() => setShowModal(true)}
                  />
                </div>
                <p className="text-xs text-indigo-200 mt-2 text-center">Click to enlarge</p>
              </div>
            )}

            {/* Loading state */}
            {isLoading && (
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 flex justify-center items-center">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full absolute border-4 border-solid border-indigo-300/30"></div>
                  <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-solid border-indigo-500 border-t-transparent"></div>
                </div>
                <span className="ml-4 text-white">Processing image...</span>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="bg-red-900/30 backdrop-blur-md rounded-xl p-6 border border-red-500/30">
                <div className="flex items-start">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-red-400 mt-1"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-red-300">Error</h3>
                    <p className="text-sm text-red-200 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          <div className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden h-full flex flex-col">
            <div className="bg-black/30 px-6 py-4 border-b border-white/10">
              <h2 className="text-xl font-semibold text-white">Conversion Results</h2>
            </div>

            {result ? (
              <div className="p-6 overflow-auto flex-1 space-y-6">
                <div>
                  <h3 className="text-sm uppercase tracking-wider text-indigo-300 font-semibold mb-3">
                    Raw Text with LaTeX:
                  </h3>
                  <pre className="bg-black/30 p-4 rounded-lg text-sm text-indigo-100 overflow-auto border border-white/10 max-h-40">
                    <code>{result}</code>
                  </pre>
                </div>

                <div>
                  <h3 className="text-sm uppercase tracking-wider text-indigo-300 font-semibold mb-3">
                    Rendered Output:
                  </h3>
                  <div className="bg-gray-400 p-4 rounded-lg border border-white/10 text-white">
                    <AIResponse
                      content={result || ""}
                      useTypewriter={true}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center p-6 text-center">
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mx-auto mb-4 text-indigo-400/50"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <line x1="10" y1="9" x2="8" y2="9"></line>
                  </svg>
                  <p className="text-indigo-200 text-lg font-medium">No Results Yet</p>
                  <p className="text-indigo-300/70 mt-2 max-w-md">
                    Upload or paste an image containing mathematical equations to see the converted text here
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Image Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/90 flex justify-center items-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div className="relative p-4 max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-white bg-indigo-600 rounded-full w-8 h-8 flex items-center justify-center hover:bg-indigo-700 transition-colors"
              onClick={() => setShowModal(false)}
            >
              ✕
            </button>
            <img
              src={imagePreview || "/placeholder.svg"}
              alt="Full Preview"
              className="max-w-full max-h-[80vh] rounded-lg object-contain bg-black/50 p-2"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default ImageTextConverter
